import React, { useState, useEffect } from 'react';
import { useEntitlements } from '../store/EntitlementStore';
import api from '../services/api';
import { Users, Plus, Edit, Lock, Unlock, Key, XCircle, RotateCw, Trash2, X, Search, ChevronRight } from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { StatusBadge } from '../components/ui/StatusBadge';
import { DataTable, DataTableEmpty } from '../components/ui/DataTable';
import './ClientsPage.css';

/**
 * Clients page - Staff-only client management.
 */
export const ClientsPage = () => {
  const { user } = useEntitlements();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [viewingClient, setViewingClient] = useState(null);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    email: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
    is_active: true,
  });
  const [enablePortalAccessOnCreate, setEnablePortalAccessOnCreate] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Check if user is staff (staff or admin role)
  const isStaff = user?.is_staff_user || user?.role === 'staff' || user?.role === 'admin';

  // Debounce search input -> searchQuery
  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (isStaff) {
      fetchClients();
    } else {
      setError('Access denied. Staff only.');
      setLoading(false);
    }
  }, [isStaff, searchQuery]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const params = searchQuery.trim() ? { search: searchQuery.trim() } : {};
      const response = await api.get('/v1/clients/', { params });
      setClients(response.data.results || response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch clients');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await api.patch(`/v1/clients/${editingClient.id}/`, formData);
        setShowModal(false);
        setEditingClient(null);
      } else {
        const response = await api.post('/v1/clients/', formData);
        const newClient = response.data;
        if (enablePortalAccessOnCreate && formData.email?.trim()) {
          try {
            const enableResponse = await api.post(`/v1/clients/${newClient.id}/enable_portal_access/`);
            alert(`Client added. Invitation email sent. Temporary password: ${enableResponse.data.temp_password}\n\nPlease save this password securely.`);
          } catch (enableErr) {
            alert(`Client added, but failed to send invitation: ${enableErr.response?.data?.error || enableErr.response?.data?.detail || 'Unknown error'}`);
          }
        } else if (enablePortalAccessOnCreate && !formData.email?.trim()) {
          alert('Client added. Email is required for portal access. Enable it later from the client view.');
        } else {
          alert('Client added.');
        }
        setShowModal(false);
        setEnablePortalAccessOnCreate(false);
      }
      setEditingClient(null);
      setFormData({
        name: '',
        company_name: '',
        email: '',
        phone: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        postal_code: '',
        country: 'US',
        is_active: true,
      });
      fetchClients();
    } catch (err) {
      // Show detailed error message from backend
      const errorData = err.response?.data;
      let errorMessage = 'Failed to save client';
      
      if (errorData) {
        if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (typeof errorData === 'object') {
          // Handle field-specific errors
          const fieldErrors = Object.entries(errorData)
            .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
            .join('; ');
          if (fieldErrors) {
            errorMessage = fieldErrors;
          }
        }
      }
      
      setError(errorMessage);
      console.error('Client save error:', err.response?.data || err);
    }
  };

  const handleView = (client) => {
    setViewingClient(client);
    setNotes([]);
    setNewNoteContent('');
    fetchNotes(client.id);
  };

  const fetchNotes = async (clientId) => {
    if (!clientId) return;
    try {
      setNotesLoading(true);
      const response = await api.get(`/v1/client-notes/?client=${clientId}`);
      const data = response.data.results || response.data || [];
      setNotes(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch notes');
      setNotes([]);
    } finally {
      setNotesLoading(false);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNoteContent.trim() || !viewingClient) return;
    try {
      setAddingNote(true);
      await api.post('/v1/client-notes/', {
        client: viewingClient.id,
        content: newNoteContent.trim(),
      });
      setNewNoteContent('');
      fetchNotes(viewingClient.id);
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.content?.[0] || 'Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (note) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await api.delete(`/v1/client-notes/${note.id}/`);
      fetchNotes(viewingClient?.id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete note');
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name || '',
      company_name: client.company_name || '',
      email: client.email || '',
      phone: client.phone || '',
      address_line1: client.address_line1 || '',
      address_line2: client.address_line2 || '',
      city: client.city || '',
      state: client.state || '',
      postal_code: client.postal_code || '',
      country: client.country || 'US',
      is_active: client.is_active !== false,
    });
    setShowModal(true);
  };

  const handleToggleActive = async (client) => {
    try {
      await api.patch(`/v1/clients/${client.id}/`, {
        is_active: !client.is_active,
      });
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update client');
    }
  };

  const handleEnablePortalAccess = async (client) => {
    if (!window.confirm(`Enable portal access for ${client.name}? An invitation email will be sent.`)) {
      return;
    }
    try {
      const response = await api.post(`/v1/clients/${client.id}/enable_portal_access/`);
      alert(`Portal access enabled. Temporary password: ${response.data.temp_password}\n\nPlease save this password securely.`);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.error || 'Failed to enable portal access');
    }
  };

  const handleDisablePortalAccess = async (client) => {
    if (!window.confirm(`Disable portal access for ${client.name}? This will immediately revoke their access.`)) {
      return;
    }
    try {
      await api.post(`/v1/clients/${client.id}/disable_portal_access/`);
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.error || 'Failed to disable portal access');
    }
  };

  const handleSendPasswordReset = async (client) => {
    if (!window.confirm(`Send password reset to ${client.name}? A temporary password will be emailed to ${client.email}.`)) {
      return;
    }
    try {
      const response = await api.post(`/v1/clients/${client.id}/send_password_reset/`);
      if (response.data.email_sent) {
        alert(`Password reset email sent successfully to ${client.email}`);
      } else {
        alert(`Password was reset but email failed to send. Please contact the client manually.`);
      }
      fetchClients();
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.response?.data?.detail || 'Failed to send password reset';
      setError(errorMsg);
    }
  };

  if (!isStaff) {
    return (
      <div className="clients-page">
        <div className="error-message">
          <Lock size={24} />
          <p>Access denied. This page is for staff only.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="clients-page">
        <div className="loading">Loading clients...</div>
      </div>
    );
  }

  return (
    <div className="clients-page">
      <PageHeader
        icon={Users}
        title="Clients"
        subtitle="Manage client records, portal access, and account status."
        primaryAction={(
          <button
            className="btn-primary"
            onClick={() => {
              setEditingClient(null);
              setEnablePortalAccessOnCreate(false);
              setFormData({
                name: '',
                company_name: '',
                email: '',
                phone: '',
                address_line1: '',
                address_line2: '',
                city: '',
                state: '',
                postal_code: '',
                country: 'US',
                is_active: true,
              });
              setShowModal(true);
            }}
          >
            <Plus size={16} />
            Add Client
          </button>
        )}
        controls={(
          <div className="clients-search">
            <Search size={18} className="clients-search-icon" />
            <input
              type="text"
              className="clients-search-input"
              placeholder="Search by name, email, or company..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
        )}
      />

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <DataTable className="clients-list">
        {clients.length === 0 ? (
          <DataTableEmpty>
            <div className="portalr-emptyState">
            <div className="portalr-emptyStateIcon" aria-hidden="true">
              <Users size={20} />
            </div>
            <div className="portalr-emptyStateTitle">
              {searchQuery.trim() ? 'No matches found' : 'Your client list is empty'}
            </div>
            <p className="portalr-emptyStateDesc">
              {searchQuery.trim()
                ? 'Try a different search term or clear filters.'
                : 'Add your first client to start sharing documents, messages, and tasks.'}
            </p>
            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              {searchQuery.trim() ? (
                <button
                  type="button"
                  className="portalr-btn portalr-btn--secondary"
                  onClick={() => {
                    setSearchInput('');
                    setSearchQuery('');
                  }}
                >
                  Clear search
                  <ChevronRight size={18} aria-hidden="true" />
                </button>
              ) : (
                <button
                  type="button"
                  className="portalr-btn portalr-btn--primary"
                  onClick={() => {
                    setEditingClient(null);
                    setEnablePortalAccessOnCreate(false);
                    setFormData({
                      name: '',
                      company_name: '',
                      email: '',
                      phone: '',
                      address_line1: '',
                      address_line2: '',
                      city: '',
                      state: '',
                      postal_code: '',
                      country: 'US',
                      is_active: true,
                    });
                    setShowModal(true);
                  }}
                >
                  Add Client
                  <ChevronRight size={18} aria-hidden="true" />
                </button>
              )}
            </div>
            </div>
          </DataTableEmpty>
        ) : (
          <table className="clients-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Company</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Portal Access</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((client) => (
                <tr key={client.id}>
                  <td data-label="Name">
                    <button
                      type="button"
                      className="client-name-link"
                      onClick={() => handleView(client)}
                    >
                      {client.name}
                    </button>
                  </td>
                  <td data-label="Company">{client.company_name || '-'}</td>
                  <td data-label="Email">{client.email || '-'}</td>
                  <td data-label="Phone">{client.phone || '-'}</td>
                  <td data-label="Status">
                    <StatusBadge
                      label={client.is_active ? 'Active' : 'Inactive'}
                      variant={client.is_active ? 'success' : 'danger'}
                    />
                  </td>
                  <td data-label="Portal Access">
                    <StatusBadge
                      label={client.portal_access_enabled ? 'Enabled' : 'Disabled'}
                      variant={client.portal_access_enabled ? 'success' : 'warning'}
                    />
                  </td>
                  <td data-label="Actions">
                    <div className="action-buttons">
                      <button
                        className="btn-icon"
                        onClick={() => handleEdit(client)}
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      {client.portal_access_enabled ? (
                        <>
                          <button
                            className="btn-icon"
                            onClick={() => handleSendPasswordReset(client)}
                            title="Send Password Reset"
                            disabled={!client.email}
                          >
                            <RotateCw size={16} />
                          </button>
                          <button
                            className="btn-icon"
                            onClick={() => handleDisablePortalAccess(client)}
                            title="Disable Portal Access"
                          >
                            <XCircle size={16} />
                          </button>
                        </>
                      ) : (
                        <button
                          className="btn-icon"
                          onClick={() => handleEnablePortalAccess(client)}
                          title="Enable Portal Access"
                          disabled={!client.email}
                        >
                          <Key size={16} />
                        </button>
                      )}
                      <button
                        className="btn-icon"
                        onClick={() => handleToggleActive(client)}
                        title={client.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {client.is_active ? <Lock size={16} /> : <Unlock size={16} />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DataTable>

      {/* Client Detail Panel (View + Notes) */}
      {viewingClient && (
        <div className="detail-panel-overlay" onClick={() => setViewingClient(null)}>
          <div className="detail-panel" onClick={(e) => e.stopPropagation()}>
            <div className="detail-panel-header">
              <h2>{viewingClient.name}</h2>
              <button
                className="detail-panel-close"
                onClick={() => setViewingClient(null)}
                title="Close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="detail-panel-body">
              <section className="detail-section">
                <h3>Client Information</h3>
                <div className="detail-grid">
                  <div className="detail-field">
                    <label>Name</label>
                    <span>{viewingClient.name}</span>
                  </div>
                  <div className="detail-field">
                    <label>Company</label>
                    <span>{viewingClient.company_name || '-'}</span>
                  </div>
                  <div className="detail-field">
                    <label>Email</label>
                    <span>{viewingClient.email || '-'}</span>
                  </div>
                  <div className="detail-field">
                    <label>Phone</label>
                    <span>{viewingClient.phone || '-'}</span>
                  </div>
                  <div className="detail-field detail-field-full">
                    <label>Address</label>
                    <span>
                      {[
                        viewingClient.address_line1,
                        viewingClient.address_line2,
                        [viewingClient.city, viewingClient.state, viewingClient.postal_code].filter(Boolean).join(', '),
                        viewingClient.country,
                      ]
                        .filter(Boolean)
                        .join(' ') || '-'}
                    </span>
                  </div>
                  <div className="detail-field">
                    <label>Status</label>
                    <StatusBadge
                      label={viewingClient.is_active ? 'Active' : 'Inactive'}
                      variant={viewingClient.is_active ? 'success' : 'danger'}
                    />
                  </div>
                  <div className="detail-field">
                    <label>Portal Access</label>
                    <StatusBadge
                      label={viewingClient.portal_access_enabled ? 'Enabled' : 'Disabled'}
                      variant={viewingClient.portal_access_enabled ? 'success' : 'warning'}
                    />
                  </div>
                </div>
                <div className="detail-panel-actions">
                  <button className="btn-primary" onClick={() => { setViewingClient(null); handleEdit(viewingClient); }}>
                    <Edit size={16} />
                    Edit Client
                  </button>
                </div>
              </section>

              <section className="detail-section detail-notes-section">
                <h3>Notes</h3>
                {notesLoading ? (
                  <div className="notes-loading">Loading notes...</div>
                ) : (
                  <>
                    <div className="notes-list">
                      {notes.length === 0 ? (
                        <div className="notes-empty">No notes yet. Add one below.</div>
                      ) : (
                        notes.map((note) => (
                          <div key={note.id} className="note-item">
                            <div className="note-content">{note.content}</div>
                            <div className="note-meta">
                              <span className="note-author">{note.created_by_email || 'Unknown'}</span>
                              <span className="note-date">
                                {note.created_at
                                  ? new Date(note.created_at).toLocaleString()
                                  : ''}
                              </span>
                              <button
                                className="note-delete btn-icon"
                                onClick={() => handleDeleteNote(note)}
                                title="Delete note"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    <form className="notes-add-form" onSubmit={handleAddNote}>
                      <textarea
                        value={newNoteContent}
                        onChange={(e) => setNewNoteContent(e.target.value)}
                        placeholder="Add a note..."
                        rows={3}
                        disabled={addingNote}
                      />
                      <button type="submit" className="btn-primary" disabled={!newNoteContent.trim() || addingNote}>
                        {addingNote ? 'Adding...' : 'Add Note'}
                      </button>
                    </form>
                  </>
                )}
              </section>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => { setEnablePortalAccessOnCreate(false); setShowModal(false); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingClient ? 'Edit Client' : 'Add Client'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Company Name</label>
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  placeholder="Optional"
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Required only if enabling portal access"
                />
              </div>
              {!editingClient && (
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={enablePortalAccessOnCreate}
                      onChange={(e) => setEnablePortalAccessOnCreate(e.target.checked)}
                    />
                    Enable portal access and send invitation email
                  </label>
                  <small className="form-help">
                    Client will receive an email with temporary password. Leave unchecked to add client without sending any notification.
                  </small>
                </div>
              )}
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Address Line 1</label>
                  <input
                    type="text"
                    value={formData.address_line1}
                    onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Address Line 2</label>
                  <input
                    type="text"
                    value={formData.address_line2}
                    onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Postal Code</label>
                  <input
                    type="text"
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  Active
                </label>
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => { setEnablePortalAccessOnCreate(false); setShowModal(false); }}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingClient ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

